import { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import type { useBoard } from './useBoard';
import { ALL, TABS } from './viewPrefs';
import { describeFreshness, freshnessColor, isSettling } from './freshness';

/**
 * The two views the board is worked from: "By epic" is Andy's, and filtering to
 * Epic = GTM plan reproduces the "The plan" view James reads.
 *
 * These are fixed in code, not user-editable: `/v1/views` is listed in the charter's API
 * surface but no handler serves it, and a server-side view store pretending to be shared
 * would be worse than an honest hardcoded one. (An earlier comment here claimed they
 * lived in localStorage — they never have. What does now is the *current* selection, in
 * `useViewPrefs`, which is a different thing.)
 */
const SAVED_VIEWS: { name: string; epic: string }[] = [
  { name: 'By epic', epic: ALL },
  { name: 'The plan', epic: 'GTM plan' },
  { name: 'Engine health', epic: 'Engine health' },
];

/**
 * A dot that fades green → amber → red as the board's data ages. Replaces the text
 * version, which cost 111px of a toolbar that had run out of room; the dot is ~26px and
 * reads at a glance, which is all this ever needed to do.
 *
 * It ticks on its own timer rather than riding the board's re-renders: `lastUpdated` only
 * moves when a poll SUCCEEDS, so a colour driven by that alone would stay green while the
 * board quietly went stale — the exact failure it exists to expose. 5s rather than 15s
 * now that the output is a gradient rather than a rounded label.
 *
 * Colour is never the only channel: the tooltip and the accessible name both carry the
 * same fact in words, so this is not information conveyed by hue alone.
 */
function FreshnessDot({ at, refreshingSince }: { at: number | null; refreshingSince: number | null }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => setTick((n) => n + 1), 5_000);
    return () => window.clearInterval(timer);
  }, []);

  if (at === null) return null;
  const now = Date.now();
  const { label, stale } = describeFreshness(at, now);

  // Time spent hidden counts toward the age, but polling is suspended while hidden BY
  // DESIGN — so a tab returned to after ten minutes would show red for the one case that
  // is not a failure, right as you look at it. A refresh IS in flight, so say so and show
  // it as healthy — but only while that refresh is plausibly still alive (`isSettling`).
  const settling = isSettling(refreshingSince, now);
  const text = settling
    ? 'Refreshing…'
    : stale
      ? `Not updating — last ${label}`
      : `Updated ${label}`;

  return (
    <Tooltip title={text}>
      {/*
        `role="img"` rather than `status`: status is an aria-live region, and this element
        has no content — only an aria-label that changes every tick. A live region would
        either announce nothing or re-announce the age every few seconds. `img` gives it a
        stable name, read when reached.

        `tabIndex` because the Tooltip is otherwise hover-only, and green→red is precisely
        the axis a deuteranope cannot separate — "colour is not the only channel" is only
        true if the words are reachable without a mouse.
      */}
      <Box
        role="img"
        aria-label={text}
        tabIndex={0}
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          flexShrink: 0,
          backgroundColor: settling ? freshnessColor(now, now) : freshnessColor(at, now),
          transition: 'background-color 2s linear',
        }}
      />
    </Tooltip>
  );
}

/**
 * Email, Connect agent and Sign out cost ~360px of permanent toolbar between them, for
 * things looked at rarely or never — and that is what tipped the bar onto a second row
 * once the freshness indicator arrived. Behind an avatar they cost ~40px.
 *
 * Nothing is actually hidden that mattered: the address is the first thing in the menu,
 * so "which account is this?" goes from zero clicks to one, and it buys back a row of
 * vertical space on every screen narrower than 1148px.
 */
function AccountMenu({
  email,
  signOut,
  onConnect,
}: {
  email: string | null;
  signOut: () => void;
  onConnect: () => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = () => setAnchor(null);
  const run = (action: () => void) => () => {
    close();
    action();
  };

  return (
    <>
      <Tooltip title={email ?? 'Account'}>
        <IconButton
          size="small"
          onClick={(event) => setAnchor(event.currentTarget)}
          aria-label={email ? `Account: ${email}` : 'Account'}
          aria-haspopup="menu"
          aria-expanded={anchor ? true : undefined}
        >
          <Avatar sx={{ width: 30, height: 30, fontSize: 14 }}>
            {(email ?? '?').charAt(0).toUpperCase()}
          </Avatar>
        </IconButton>
      </Tooltip>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        {/* Not a MenuItem: it is a label, and making it focusable would put a no-op stop
            in the keyboard path between opening the menu and the two real actions. */}
        <Box sx={{ px: 2, py: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {email}
          </Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={run(onConnect)}>Connect agent</MenuItem>
        <MenuItem onClick={run(signOut)}>Sign out</MenuItem>
      </Menu>
    </>
  );
}

/**
 * The width above which the toolbar fits on ONE ROW with the chips shown. A bar that
 * wraps to two rows is the failure this number exists to prevent (DWLF-109, DWLF-168).
 *
 * A measured number rather than a breakpoint token: the scale's `lg` is 1200, which is
 * less than the bar needs, so a token here reads as a design decision while actually
 * being a wrap.
 *
 * Re-measure whenever the bar gains a control. ⚠️ **In a window wider than this value,
 * with a single epic selected** — those are not incidental. Narrower, the chips are
 * `display: none` and measure as zero, so the snippet returns the no-chips width and
 * confidently under-reports by the width of the thing being measured; without an epic,
 * the "Open epic" button is absent and it under-reports again.
 *
 *   const bar = document.querySelector('header .MuiToolbar-root');
 *   const fixed = [...bar.children].filter((k) => getComputedStyle(k).flexGrow !== '1');
 *   fixed.reduce((w, k) => w + k.getBoundingClientRect().width, 0)
 *     + 12 * (bar.children.length - 1) + 48;
 */
const CHIPS_FIT_ABOVE_PX = 1400;

/** Derived, so the query and the measured number cannot drift apart. */
const CHIPS_SHOWN = `@media (min-width:${CHIPS_FIT_ABOVE_PX}px)`;

/**
 * The chips are shortcuts, not capabilities: every one sets `epic` to a value the Epic
 * dropdown already offers, so hiding them costs a click rather than a feature. That
 * redundancy is exactly why they are the right thing to drop when space is short — and
 * they are the widest item on the bar.
 */
function SavedViewChips({ epic, onPick }: { epic: string; onPick: (epic: string) => void }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ display: 'none', [CHIPS_SHOWN]: { display: 'flex' } }}
    >
      {SAVED_VIEWS.map((view) => (
        <Chip
          key={view.name}
          label={view.name}
          size="small"
          variant={epic === view.epic ? 'filled' : 'outlined'}
          onClick={() => onPick(view.epic)}
        />
      ))}
    </Stack>
  );
}

export interface BarProps {
  tab: number;
  chooseTab: (next: number) => void;
  filters: { epic: string; status: string };
  chooseFilters: (next: { epic: string; status: string }) => void;
  meta: ReturnType<typeof useBoard>['meta'];
  lastUpdated: number | null;
  refreshingSince: number | null;
  email: string | null;
  signOut: () => void;
  onNew: () => void;
  onConnect: () => void;
  onOpenEpic: (name: string) => void;
}

export function BoardBar({
  tab,
  chooseTab,
  filters,
  chooseFilters,
  meta,
  lastUpdated,
  refreshingSince,
  email,
  signOut,
  onNew,
  onConnect,
  onOpenEpic,
}: BarProps) {
  const select = (label: string, key: 'epic' | 'status', options: string[]) => {
    const value = filters[key];
    // A Select whose value is absent from its own options renders blank and logs an
    // out-of-range warning. That is the normal case for one render on every refresh: the
    // selection is restored from localStorage synchronously, while the options arrive a
    // /v1/meta request later. Carrying the value keeps the control honest in the gap —
    // and if it turns out to be a retired enum, useViewPrefs clears it once meta lands.
    const shown = value !== ALL && !options.includes(value) ? [value, ...options] : options;
    // Named once: the two `onClick`s exist only to match the `onChange`, and all three
    // have to stay in step. Same reason as the tabs — re-picking the value already held
    // fires no change event.
    //
    // ⚠️ The MenuItem `onClick` reaching us is MUI's `SelectInput` choosing to forward
    // `child.props.onClick`, which is not part of the documented Select API. Verify on a
    // MUI upgrade — if it stops forwarding, re-picking the current value silently goes
    // back to doing nothing (DWLF-144).
    const choose = (next: string) => chooseFilters({ ...filters, [key]: next });

    return (
      <TextField
        select
        size="small"
        label={label}
        value={value}
        onChange={(event) => choose(event.target.value)}
        sx={{ minWidth: 145 }}
      >
        <MenuItem value={ALL} onClick={() => choose(ALL)}>
          {ALL}
        </MenuItem>
        {shown.map((option) => (
          <MenuItem key={option} value={option} onClick={() => choose(option)}>
            {option}
          </MenuItem>
        ))}
      </TextField>
    );
  };

  return (
    <Toolbar sx={{ gap: 1.5, flexWrap: 'wrap' }}>
      <Typography
        variant="h6"
        fontWeight={600}
        sx={{ mr: 1, display: { xs: 'none', sm: 'block' } }}
      >
        DWLF Tickets
      </Typography>

      {/*
        `onClick` as well as `onChange`: MUI's Tab fires no change event when the tab
        clicked is the one already selected, and that click still has to reach the board.
        Both handlers firing on a real change is harmless — see `BoardControlsInput.navigate`.
        `onChange` is the documented Tabs API and carries what the click path does not,
        `selectionFollowsFocus` among them.
      */}
      <Tabs value={tab} onChange={(_, next) => chooseTab(next)}>
        {TABS.map((entry, index) => (
          // `value` explicitly, so the index `onChange` reports is the same one `onClick`
          // sends. MUI falls back to its own child index when `value` is undefined, and
          // that only coincides with this one while every child is a valid element.
          <Tab
            key={entry.key}
            value={index}
            label={entry.label}
            onClick={() => chooseTab(index)}
            // MUI reserves 90px per tab whatever the label is, and these are one word
            // each. The room that buys back is what keeps the bar on one row once the
            // chips have already given way — the last thing there is left to trade
            // before it wraps (DWLF-168).
            sx={{ minWidth: 'auto', px: 1.5 }}
          />
        ))}
      </Tabs>

      <Box sx={{ flex: 1 }} />

      <SavedViewChips epic={filters.epic} onPick={(epic) => chooseFilters({ ...filters, epic })} />

      {select('Epic', 'epic', meta?.epics ?? [])}
      {select('Status', 'status', meta?.statuses ?? [])}

      {/* Only when one epic is selected: the page is about a single epic, and there is
          nothing to open when the filter says All. */}
      {filters.epic !== ALL && (
        <Button size="small" onClick={() => onOpenEpic(filters.epic)}>
          Open epic
        </Button>
      )}

      <Button variant="contained" size="small" onClick={onNew}>
        New ticket
      </Button>

      <FreshnessDot at={lastUpdated} refreshingSince={refreshingSince} />

      <AccountMenu email={email} signOut={signOut} onConnect={onConnect} />
    </Toolbar>
  );
}
